import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Spinner
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import SearchViewApi from '../../../api/SearchViewApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import SearchViewDialog from './SearchViewDialog';
import PubSub from 'pubsub-js';
import { getEntitlementsSection } from '../../../utils/entitlements';

const SearchViewManager = () => {
  const navigate = useNavigate();
  const searchViewEnt = getEntitlementsSection('searchView', {});
  const allowViewSearchView = searchViewEnt.allowViewSearchView !== false;
  const allowCreateSearchView = searchViewEnt.allowCreateSearchView !== false;
  // Empty array = no restriction (same as missing); show all active objects from the schema API.
  const rawAllowed = searchViewEnt.allowedObjects;
  const allowedObjectsList =
    Array.isArray(rawAllowed) && rawAllowed.length > 0 ? rawAllowed : null;
  const allowedObjectsSet = useMemo(
    () => (allowedObjectsList === null ? null : new Set(allowedObjectsList.map((o) => (o || '').toLowerCase()))),
    [allowedObjectsList]
  );

  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [searchViewData, setSearchViewData] = useState(null);

  const visibleObjects = useMemo(() => {
    if (allowedObjectsSet === null) return objects;
    return objects.filter((o) => allowedObjectsSet.has((o.object_name || '').toLowerCase()));
  }, [objects, allowedObjectsSet]);

  useEffect(() => {
    if (!allowViewSearchView) {
      navigate('/setup', { replace: true });
      return;
    }
    fetchObjectsWithStatus();

    // Subscribe to save events
    const token = PubSub.subscribe('SEARCH_VIEW_SAVED', () => {
      fetchObjectsWithStatus();
      setShowDialog(false);
      setEditingObject(null);
      setSearchViewData(null);
    });

    return () => {
      PubSub.unsubscribe(token);
    };
  }, [allowViewSearchView, navigate]);

  const fetchObjectsWithStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.getObjectsWithStatus();

      if (response?.data?.success) {
        setObjects(response.data.data || []);
      } else {
        setError(response?.data?.message || 'Failed to fetch objects');
      }
    } catch (err) {
      console.error('Error fetching objects with search view status:', err);
      setError(err.response?.data?.message || 'Failed to fetch objects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingObject(null);
    setSearchViewData(null);
    setShowDialog(true);
  };

  const handleEdit = async (searchViewId) => {
    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.fetchById(searchViewId);

      if (response?.data?.success) {
        setSearchViewData(response.data.data);
        setEditingObject(response.data.data.object_name);
        setShowDialog(true);
      } else {
        setError('Failed to fetch search view configuration');
      }
    } catch (err) {
      console.error('Error fetching search view:', err);
      setError(err.response?.data?.message || 'Failed to fetch search view');
    }
  };

  const handleDelete = async (objectName, searchViewId, viewLabel) => {
    const labelHint = viewLabel ? ` (“${viewLabel}”)` : '';
    if (!window.confirm(`Delete this search view for ${objectName}${labelHint}?`)) {
      return;
    }

    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.delete(searchViewId);

      if (response?.data?.success) {
        setSuccess('Search view deleted successfully');
        fetchObjectsWithStatus();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.data?.message || 'Failed to delete search view');
      }
    } catch (err) {
      console.error('Error deleting search view:', err);
      setError(err.response?.data?.message || 'Failed to delete search view');
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditingObject(null);
    setSearchViewData(null);
  };

  if (!allowViewSearchView) return null;

  if (loading) {
    return (
      <Container fluid className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading search views...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <FaSearch className="me-2" />
                Search View Configuration
              </h4>
              {allowCreateSearchView && (
                <Button variant="primary" onClick={handleCreate}>
                  <FaPlus className="me-2" />
                  Configure Search View
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Object</th>
                    <th>View label</th>
                    <th>Search name (URL)</th>
                    <th>Status</th>
                    <th>Display Fields</th>
                    <th>Filterable Fields</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleObjects.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        {allowCreateSearchView ? 'No objects found. Configure a search view to get started.' : 'No objects available for search views.'}
                      </td>
                    </tr>
                  ) : (
                    visibleObjects.map((obj) => (
                      <tr key={obj.search_view_id ? `${obj.object_name}-${obj.search_view_id}` : obj.object_name}>
                        <td>
                          <strong>{obj.object_label || obj.object_name}</strong>
                          {obj.is_standard === false ? (
                            <Badge bg="info" className="ms-2">
                              Custom
                            </Badge>
                          ) : (
                            <Badge pill bg="secondary" className="ms-2">
                              Standard
                            </Badge>
                          )}
                          <br />
                          <small className="text-muted">{obj.object_name}</small>
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <span>{obj.view_label || <span className="text-muted">—</span>}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {obj.has_search_view && obj.search_view_name ? (
                            <code className="small">/search/{obj.object_name}/{obj.search_view_name}</code>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <Badge bg="success">
                              <FaCheckCircle className="me-1" />
                              Configured
                            </Badge>
                          ) : (
                            <Badge bg="secondary">
                              <FaTimesCircle className="me-1" />
                              Not Configured
                            </Badge>
                          )}
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <Badge bg="info">Configured</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <Badge bg="info">Configured</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {allowCreateSearchView && (
                            obj.has_search_view ? (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEdit(obj.search_view_id)}
                                >
                                  <FaEdit className="me-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleDelete(obj.object_name, obj.search_view_id, obj.view_label)}
                                >
                                  <FaTrash className="me-1" />
                                  Delete
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    setEditingObject(obj.object_name);
                                    setSearchViewData(null);
                                    setShowDialog(true);
                                  }}
                                >
                                  <FaPlus className="me-1" />
                                  Add another
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  setEditingObject(obj.object_name);
                                  setSearchViewData(null);
                                  setShowDialog(true);
                                }}
                              >
                                <FaPlus className="me-1" />
                                Configure
                              </Button>
                            )
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SearchViewDialog
        show={showDialog}
        onClose={handleDialogClose}
        objectName={editingObject}
        searchViewData={searchViewData}
      />
    </Container>
  );
};

export default SearchViewManager;
